import {
  SectionTitle,
  SectionTitleSize,
  SectionTitleStyle
} from '../SectionTitle/SectionTitle'
import { SectionText } from '../Text/Text'
import { CMSSection, CMSSectionBackground } from '../CMSSection/CMSSection'
import { SectionLink } from '../SectionLink/SectionLink'
import { BACKEND_URL } from '../../util'
import { ListItem, OrderedList, UnorderedList } from '../lists/lists'
import type { JSONValue } from '../../util'
import {
  SectionAccordion,
  SectionAccordionContentType
} from '../SectionAccordion/SectionAccordion'
import { Table } from '../Table/Table'
import type { Cell } from '../Table/Table'
import { SectionGuide } from '../SectionGuide/SectionGuide'
import type { GuideCard } from '../SectionGuide/SectionGuide'
import { Hero } from '../Hero/Hero'
import { ContractValueChart } from '../ContractValueChart/ContractValueChart'
import { Breadcrumb } from '../Breadcrumb/Breadcrumb'
import { PageTitle } from '../PageTitle/PageTitle'
import { RequestCallback } from '../RequestCallback/RequestCallback'
import { TeamMember, TeamMemberProps } from '../TeamMember/TeamMember'
import { Team } from '../Team/Team'
import { Chairman } from '../Chairman/Chairman'
import { CarouselItem, Carousel } from '../Carousel/Carousel'
import { GridGallery, GridGalleryItem } from '../GridGallery/GridGallery'
import gridGalleryStyles from '../GridGallery/GridGallery.module.css'
import { Link } from '../Link/Link'
import { Partner, Partners } from '../Partners/Partners'
import { YouTube } from '../YouTube/YouTube'
import { KpiChart } from '../KpiChart'
import { cornerClass } from '../ImageCorners/ImageCorners'
import type { CornerStyle } from '../ImageCorners/ImageCorners'

type BlockInstance = {
  name: string
  content: { [field: string]: JSONValue }
}

function Block({ block, background }: { block: BlockInstance, background?: CMSSectionBackground }) {
  switch (block.name) {
    case 'Brands':
      return (
        <Partners
          partners={block.content.brands as Partner[]}
        />
      )
    case 'Hero':
      return (
        <Hero
          title={block.content.title as string}
          subtitle={block.content.subtitle as string}
          content={block.content.content as string ?? ''}
          image={block.content.image as string}
          overlayColor={block.content.overlayColor as string}
          overlayOpacity={block.content.overlayOpacity as number}
          showText={block.content.showText as boolean}
          showButton={block.content.showButton as boolean}
          textAlign={block.content.textAlign as 'left' | 'center' | 'right'}
          textColor={block.content.textColor as string}
          buttonText={block.content.buttonText as string}
          buttonLink={
            block.content.buttonLink === '__custom__'
              ? block.content.buttonLinkCustom as string
              : block.content.buttonLink as string
          }
        />
      )
    case 'Page Title':
      return (
        <PageTitle title={block.content.title as string} subtitle={block.content.subtitle as string}/>
      )
    case 'Section Link':
      return (
        <SectionLink
          path={block.content.link as string}
          label={block.content.label as string}
        />
      )
    case 'Title Link':
      return <Link href={`#${block.content.reference}`}>{block.content.label}</Link>
    case 'Section Title':
      return (
        <SectionTitle
          title={block.content.title as string}
          subtitle={block.content.subtitle as string}
          reference={block.content.reference as string}
          size={(block.content.size ?? 'medium') as SectionTitleSize}
          style={background === CMSSectionBackground.Blue ? SectionTitleStyle.Light : SectionTitleStyle.Dark}
        />
      )
    case 'Textarea':
      return <SectionText style={{...(background === CMSSectionBackground.Blue ? {color: '#fff'} : {})}}>{block.content.text}</SectionText>
    case 'Image':
      return (
        <img
          src={`${BACKEND_URL}file/${block.content.image}`}
          alt={(block.content.alt as string) ?? ''}
          className={cornerClass(block.content.cornerStyle) || undefined}
        />
      )
    case 'File': {
      const fileHref = `${BACKEND_URL}file/${block.content.fileId}`
      const imageId = block.content.imageId as string | undefined
      if (imageId) {
        return (
          <Link
            href={fileHref}
            className={`${gridGalleryStyles.item} ${cornerClass(block.content.cornerStyle, { tile: true, clip: true })}`}
          >
            <div className={gridGalleryStyles.imageContainer}>
              <img
                className={gridGalleryStyles.image}
                src={`${BACKEND_URL}file/${imageId}`}
                alt={(block.content.label as string) ?? ''}
              />
              <div className={gridGalleryStyles.gradient}></div>
              <div className={gridGalleryStyles.overlay}></div>
              <div className={gridGalleryStyles.textContainer}>
                <div className={gridGalleryStyles.caption}>{block.content.label}</div>
                {block.content.caption ? (
                  <div className={gridGalleryStyles.description}>{block.content.caption}</div>
                ) : null}
              </div>
            </div>
          </Link>
        )
      }
      return <Link href={fileHref}>{block.content.label}</Link>
    }
    case 'External Link':
      return <Link href={block.content.link as string}>{block.content.label}</Link>
    case 'List': {
      const List =
        block.content.style === 'bullet' ? UnorderedList : OrderedList
      return (
        <List>
          {(block.content.items as string[]).map((item, index) => (
            <ListItem key={index}>{item}</ListItem>
          ))}
        </List>
      )
    }
    case 'Accordion': {
      return (
        <SectionAccordion
          content={
            block.content as {
              cards: {
                style: SectionAccordionContentType
                title: string
                items: string[]
              }[]
            }
          }
        />
      )
    }
    case 'Carousel': {
      return (
        <Carousel items={block.content.cards as CarouselItem[]}/>
      )
    }
    case 'Grid Gallery': {
      return (
        <GridGallery 
          items={block.content.items as GridGalleryItem[]}
          elementsPerRow={block.content.elementsPerRow as number}
        />
      )
    }
    case 'Table': {
      return <Table table={block.content.table as unknown as Cell[][]} />
    }
    case 'Guide': {
      return (
        <SectionGuide cards={block.content.cards as unknown as GuideCard[]} />
      )
    }
    case 'Breadcrumb': {
      return (
        <Breadcrumb/>
      )
    }
    case 'Section': {
      const background =
        block.content.background === 'blue'
          ? CMSSectionBackground.Blue
          : block.content.background === 'white'
            ? CMSSectionBackground.White
            : CMSSectionBackground.Grey

      return (
        <CMSSection
          background={background}
          primary={((block.content.primary ?? []) as BlockInstance[]).map((subBlock, index) => {
            return <Block key={`${index}_${subBlock.name}`} block={subBlock} background={background}/>
          })}
          secondary={((block.content.secondary ?? []) as BlockInstance[]).map((subBlock, index) => {
            return <Block key={`${index}_${subBlock.name}`} block={subBlock} background={background}/>
          })}
          reversed={block.content.reversed as boolean ?? false}
        />
      )
    }
    case 'Request Callback':
      return (
        <RequestCallback/>
      )
    case 'Team':
      return (
        <Team>
          {(block.content.team as TeamMemberProps[]).map(member => {
            return (
              <TeamMember
                name={member.name}
                position={member.position}
                photoId={`${BACKEND_URL}file/${member.photoId}`}
                cornerStyle={member.cornerStyle}
              />
            )
          })}
        </Team>
      )
    case 'Featured Person':
      return (
        <Chairman
          image={`${BACKEND_URL}file/${block.content.photo}`}
          name={block.content.name as string}
          position={block.content.position as string}
          cornerStyle={block.content.cornerStyle as CornerStyle | undefined}
        />
      )
    case 'YouTube':
      return (
        <YouTube
          videoUrl={block.content.videoUrl as string}
          title={block.content.title as string}
        />
      )
    case 'KPI Achievement of All Targets':
      return <KpiChart chartType="overallAchievement" {...block.content} />
    case 'KPI Customer Satisfaction':
      return <KpiChart chartType="customerSatisfaction" {...block.content} />
    case 'KPI Certification':
      return <KpiChart chartType="certification" {...block.content} />
    case 'KPI Satisfaction Percentage':
      return <KpiChart chartType="satisfactionPercentage" {...block.content} />
    case 'KPI Complaints Tracking':
      return <KpiChart chartType="complaintsCount" {...block.content} />
    case 'KPI Progress Against Plan':
      return <KpiChart chartType="progressAgainstPlan" {...block.content} />
    case 'KPI Contract Value':
      return <ContractValueChart title={block.content.title as string} />
    case 'KPI Specific Metrics':
      return <KpiChart chartType="specificKpis" {...block.content} />
    case 'KPI Contractor Comparison':
      return <KpiChart chartType="contractorComparison" {...block.content} />
    case 'KPI Trend Analysis':
      return <KpiChart chartType="trendAnalysis" {...block.content} />
    case 'KPI Submission Timeliness':
      return <KpiChart chartType="submissionTimeliness" {...block.content} />
    default:
      throw new Error('No such Section BlockInstance: ' + block.name)
  }
}

export { Block }
export type { BlockInstance }
